import React from 'react';
import classNames from 'classnames';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { Ace } from '@/accessControl/itemAclModal.utilities';
import { ACLPermissionName, PERMISSIONS } from '@/main/app.constants';

interface AceCheckboxProps {
  ace: Ace;
  setPermissions: (permission: ACLPermissionName, ace: Ace) => void;
  name: ACLPermissionName;
  displayOnly?: boolean;
  isAclFromDatasource: boolean;
}

export type CheckboxPlacement = 'left' | 'right';

export const AceCheckbox: React.FunctionComponent<AceCheckboxProps> = ({
  ace,
  setPermissions,
  name,
  displayOnly,
  isAclFromDatasource,
}) => {
  const hasLocalPermission = !!ace.permissions && !!ace.permissions[name];
  const hasInheritedPermission = !!ace.inheritedPermissions && !!ace.inheritedPermissions[name];
  const isOwner = !!ace.isOwner;
  const inherited: boolean = isOwner && hasInheritedPermission;
  const placementForPermission: CheckboxPlacement = name === PERMISSIONS.READ ? 'right' : 'left';

  const canTogglePermissions = !inherited && !displayOnly && !isAclFromDatasource;
  const togglePermissions = () => canTogglePermissions && setPermissions(name, ace);

  /**
   * Returns a translation key that represents the appropriate tooltip text.
   * Local permissions always take precedence.
   *
   * @return the translation key or undefined if no tooltip should show
   */
  const getTooltip: () => string | undefined = () => {
    if (hasLocalPermission || displayOnly) {
      // takes precedence
    } else if (isAclFromDatasource) {
      return 'ACCESS_CONTROL.FROM_DATASOURCE';
    } else if (inherited) {
      return 'ACCESS_CONTROL.INHERITED';
    } else if (isOwner) {
      return 'ACCESS_CONTROL.OWNER_ACCESS';
    }
  };

  /**
   * Returns a string of css classes that displays the correct checkbox and cursor.
   * Local permissions show as black, inherited permission show as grayed out. Permissions that come from the
   * datasource are shown as grayed out with a "forbidden" tooltip.
   *
   * @return CSS classes that display the appropriate checkbox and cursor
   */
  const getCheckboxStyle: () => string = () => {
    const defaultCursor = 'cursorDefault';
    const cursorPointer = 'cursorPointer';
    if (hasLocalPermission || hasInheritedPermission || isOwner) {
      const checked = 'fa-sharp fa-regular fa-check-square';
      if (displayOnly) {
        return `${checked} disabledLook ${defaultCursor}`;
      }
      if (isAclFromDatasource) {
        return `${checked} disabledLook cursorNotAllowed`;
      }
      // local permissions take precedence
      if (hasLocalPermission) {
        return `${checked} ${cursorPointer}`;
      }
      return `${checked} disabledLook ${defaultCursor}`;
    } else {
      const unchecked = 'fa-sharp fa-regular fa-square';
      if (displayOnly) {
        return `${unchecked} disabledLook ${defaultCursor}`;
      }
      if (isAclFromDatasource) {
        return `${unchecked} disabledLook cursorNotAllowed`;
      }
      return `${unchecked} ${cursorPointer}`;
    }
  };

  const keyUp = (event: any) => {
    if (event.key === '32') {
      togglePermissions();
    }
  };

  return (
    <div onClick={togglePermissions}>
      <HoverTooltip delay={1000} text={getTooltip()} placement={placementForPermission}>
        <span
          id={`ace-checkbox-${name}`}
          data-testid="ace-checkbox"
          className={classNames(getCheckboxStyle())}
          onKeyUp={keyUp}
        />
      </HoverTooltip>
    </div>
  );
};
